import { ActionType } from '@enums/node-types.enum';
import { CollectiblesConfig, CollectibleInfo } from '@models/collectibles';
import { GameAdmin, AdminTheme, SceneLayoutAdmin } from '@models/game-admin';
import {
  StageInfo,
  ThemeConfig,
  PathConfig,
  ActionsConfig,
  ActionInfo,
  GameConfig,
} from '@models/game-config.interface';
import { UserPrize } from '@models/prize';
import { SessionParams } from '@models/session-params';
import { fetchWithLogs } from '@services/api/fetch-with-logs';
import { Singleton } from '@utils/singleton';

@Singleton
export class AdminAPIService {
  async fetchGameConfig(sessionParams: SessionParams): Promise<GameConfig> {
    if (
      !sessionParams ||
      !sessionParams.token ||
      !sessionParams.stageUuid ||
      !sessionParams.pathUuid
    ) {
      throw new Error('Invalid session params');
    }
    const headers = {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + sessionParams.token,
    };

    const [gameRes, layoutRes] = await Promise.all([
      fetchWithLogs(
        `${import.meta.env.VITE_MANDO_API_URL}/api/admin/board/stages/${sessionParams.stageUuid}`,
        { method: 'GET', headers },
        'Failed to fetch stage',
      ),
      fetchWithLogs(
        `${import.meta.env.VITE_MANDO_API_URL}/api/admin/board/path-associations/${sessionParams.pathUuid}/scene-layout`,
        { method: 'GET', headers },
        'Failed to fetch layout',
      ),
    ]);

    const [game, layout] = await Promise.all([
      gameRes.json(),
      layoutRes.json(),
    ]);
    return this.getGameConfig(game, sessionParams.pathUuid, layout);
  }

  private getGameConfig(
    game: GameAdmin,
    pathUuid: string,
    layout: SceneLayoutAdmin,
  ): GameConfig {
    return {
      game: {
        uuid: '',
        name: '',
        totalStages: 1,
        stages: [],
      },
      stage: this.getGameInfo(game, pathUuid, layout),
    };
  }

  private getGameInfo(
    game: GameAdmin,
    pathUuid: string,
    layout: SceneLayoutAdmin,
  ): StageInfo {
    const pathConfig = game.paths.find((p) => p.uuid === pathUuid);
    const actionNodes = pathConfig?.pathConfig.actionNodes ?? [];
    const nodesCount = pathConfig?.pathConfig.nodesCount ?? 0;
    const board: PathConfig = {
      uuid: pathConfig?.uuid ?? '',
      nodesCount: nodesCount,
      nodes: actionNodes.reduce(
        (acc, node) => {
          acc[String(node.position)] = node.action.uuid;
          return acc;
        },
        {} as Record<string, string>,
      ),
    };

    const actionsMap = new Map<string, ActionInfo>();
    for (const node of actionNodes) {
      if (!actionsMap.has(node.action.uuid)) {
        actionsMap.set(node.action.uuid, {
          uuid: node.action.uuid,
          name: node.action.name,
          gameRulesLabel: node.action.gameRulesLabel,
          prize: node.action.prize as unknown as UserPrize,
          symbolUrl: node.action.symbol.url,
          actionType: node.action.actionType as ActionType,
        });
      }
    }
    const actionsConfig: ActionsConfig = {
      uuid: '',
      actions: Array.from(actionsMap.values()),
    };

    const seenCollectibles = new Set<string>();
    const collectibles: CollectibleInfo[] = [];
    for (const node of actionNodes) {
      const c = node.action.collectible;
      if (c && !seenCollectibles.has(c.uuid)) {
        seenCollectibles.add(c.uuid);
        collectibles.push({
          uuid: c.uuid,
          name: c.name,
          iconUrl: c.icon.url,
          prize: c.prize as unknown as UserPrize,
          targetCount: c.targetCount,
        });
      }
    }
    const collectiblesConfig: CollectiblesConfig = {
      uuid: '',
      collectibles,
    };

    return {
      uuid: game.uuid,
      name: game.name,
      startTime: game.startTime,
      endTime: game.endTime,
      rollsPerDay: game.rollsPerDay,
      delayBetweenRolls: game.delayBetweenRolls,
      theme: this.getThemeConfig(game.themes[0].theme),
      pathConfig: board,
      actionsConfig,
      collectiblesConfig,
      boardGoals: [],
      pathSettings: {
        paddingX: layout.paddingX,
        amplitude: layout.amplitude,
        waves: layout.waves,
        phase: layout.phase,
        curvature: layout.curvature,
        stretch: layout.stretch,
        horizontalNoise: layout.horizontalNoise,
        verticalNoise: layout.verticalNoise,
        twist: layout.twist,
      },
      layoutAssets: layout.assets.map((a) => ({
        ...a,
        url: a.asset.url,
      })),
    };
  }

  getThemeConfig(theme: AdminTheme): ThemeConfig {
    return {
      uuid: theme.uuid,
      name: theme.name,
      darkMode: theme.darkMode,
      secondaryCtaStyle: theme.secondaryCtaStyle,
      cssVariables: theme.cssVariables.map((cv) => ({
        key: cv.key,
        value: cv.value,
      })),
      gameLogoUrl: theme.gameLogoAsset?.url ?? '',
      gamePageBackgroundUrl: theme.gamePageBackgroundAsset?.url ?? '',
      homePageBackgroundUrl: theme.homePageBackgroundAsset?.url ?? '',
      finishPageBackgroundUrl: theme.finishPageBackgroundAsset?.url ?? '',
      upcomingNodeUrl:
        theme.upcomingNodeAsset?.url ?? theme.defaultNodeAsset?.url ?? '',
      defaultNodeUrl: theme.defaultNodeAsset?.url ?? '',
      actionUpcomingNodeUrl:
        theme.actionUpcomingNodeAsset?.url ?? theme.actionNodeAsset?.url ?? '',
      actionNodeUrl: theme.actionNodeAsset?.url ?? '',
      labels: [],
    };
  }
}
